import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Award, Lock, Star, Zap, Trophy, Target, Brain, Heart, Rocket } from 'lucide-react';
import { motion } from 'framer-motion';

// Badge definitions
const allBadges = [
  {
    id: 'explorer',
    name: 'Explorer',
    description: 'Complete your first career exploration',
    icon: '🔍',
    color: 'from-blue-500 to-cyan-500',
    requirement: 'View 3 career cards',
    xpReward: 25
  },
  {
    id: 'dreamer',
    name: 'Big Dreamer',
    description: 'Add your first dream to your list',
    icon: '⭐',
    color: 'from-purple-500 to-pink-500',
    requirement: 'Add 1 dream',
    xpReward: 20
  },
  {
    id: 'quiz-master',
    name: 'Quiz Master',
    description: 'Complete the personality quiz',
    icon: '🧠',
    color: 'from-green-500 to-emerald-500',
    requirement: 'Finish personality quiz',
    xpReward: 50
  },
  {
    id: 'self-aware',
    name: 'Self-Aware',
    description: 'Identify your superpowers',
    icon: '💪',
    color: 'from-orange-500 to-red-500',
    requirement: 'Select 3 superpowers',
    xpReward: 30
  },
  {
    id: 'dream-chaser',
    name: 'Dream Chaser',
    description: 'Complete a dream from your list',
    icon: '🏆',
    color: 'from-yellow-500 to-amber-500',
    requirement: 'Complete 1 dream',
    xpReward: 100
  },
  {
    id: 'learner',
    name: 'Eager Learner',
    description: 'Visit the learning portal',
    icon: '📚',
    color: 'from-cyan-500 to-blue-500',
    requirement: 'Explore learning resources',
    xpReward: 15
  },
  {
    id: 'adventurer',
    name: 'Career Adventurer',
    description: 'Explore 10 different careers',
    icon: '🚀',
    color: 'from-indigo-500 to-purple-500',
    requirement: 'View 10 career cards',
    xpReward: 75
  },
  {
    id: 'consistent',
    name: 'Consistent',
    description: 'Complete quests 3 weeks in a row',
    icon: '🔥',
    color: 'from-red-500 to-orange-500',
    requirement: '3-week streak',
    xpReward: 150
  },
  {
    id: 'future-thinker',
    name: 'Future Thinker',
    description: 'Explore future jobs',
    icon: '🔮',
    color: 'from-violet-500 to-purple-500',
    requirement: 'View future jobs page',
    xpReward: 30
  },
  {
    id: 'program-hunter',
    name: 'Program Hunter',
    description: 'Discover cool programs',
    icon: '🎯',
    color: 'from-teal-500 to-green-500',
    requirement: 'Explore programs page',
    xpReward: 25
  },
  {
    id: 'dream-life',
    name: 'Dream Life Designer',
    description: 'Complete the dream life quiz',
    icon: '✨',
    color: 'from-pink-500 to-rose-500',
    requirement: 'Finish dream life quiz',
    xpReward: 50
  },
  {
    id: 'superstar',
    name: 'Superstar',
    description: 'Earn 500 total XP',
    icon: '🌟',
    color: 'from-yellow-400 to-orange-500',
    requirement: '500 XP earned',
    xpReward: 100
  }
];

export default function MSBadgesPage() {
  const { user, token } = useAuth();
  const [earnedBadges, setEarnedBadges] = useState(['explorer', 'dreamer']);
  const [totalXP, setTotalXP] = useState(0);

  useEffect(() => {
    // In real app, fetch from API
    setTotalXP(user?.xp_points || 0);
    // For demo, show a couple badges as earned
    const saved = localStorage.getItem('ms_badges_earned');
    if (saved) {
      setEarnedBadges(JSON.parse(saved));
    }
  }, [user]);

  const earnedCount = earnedBadges.length;
  const totalBadges = allBadges.length;

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
            <Award className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">My Badges</h1>
            <p className="text-slate-300">Collect them all!</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="glass-card p-4 text-center">
            <div className="text-3xl font-bold text-white">{earnedCount}</div>
            <p className="text-slate-300 text-sm">Badges Earned</p>
          </div>
          <div className="glass-card p-4 text-center">
            <div className="text-3xl font-bold text-yellow-400">{totalXP}</div>
            <p className="text-slate-300 text-sm">Total XP</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4 glass-card p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-300">Collection Progress</span>
            <span className="text-white font-medium">{earnedCount}/{totalBadges}</span>
          </div>
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
              initial={{ width: 0 }}
              animate={{ width: `${(earnedCount / totalBadges) * 100}%` }}
            />
          </div>
        </div>
      </motion.div>

      {/* Earned Badges */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />
          Earned ({earnedCount})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {allBadges.filter(b => earnedBadges.includes(b.id)).map((badge, index) => (
            <motion.div
              key={badge.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-xl bg-gradient-to-br ${badge.color} text-center`}
            >
              <span className="text-4xl block mb-2">{badge.icon}</span>
              <h3 className="font-bold text-white text-sm">{badge.name}</h3>
              <p className="text-white/70 text-xs mt-1">{badge.description}</p>
              <div className="mt-2 px-2 py-1 bg-white/20 rounded-full text-xs text-white">
                +{badge.xpReward} XP
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Locked Badges */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-slate-300" />
          Locked ({totalBadges - earnedCount})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {allBadges.filter(b => !earnedBadges.includes(b.id)).map((badge, index) => (
            <motion.div
              key={badge.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              className="p-4 rounded-xl bg-slate-800/50 text-center relative overflow-hidden"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <Lock className="w-8 h-8 text-slate-300" />
              </div>
              <span className="text-4xl block mb-2 opacity-30 blur-sm">{badge.icon}</span>
              <h3 className="font-bold text-slate-300 text-sm">{badge.name}</h3>
              <p className="text-slate-300 text-xs mt-1">{badge.requirement}</p>
              <div className="mt-2 px-2 py-1 bg-slate-700 rounded-full text-xs text-slate-300">
                +{badge.xpReward} XP
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Encouragement */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-center"
      >
        <p className="text-slate-300">
          Keep exploring and learning to unlock more badges! 🎯
        </p>
      </motion.div>
    </div>
  );
}
